import React, { useLayoutEffect, useRef } from 'react';
import styles from './Toolbar.module.css';
import editorStyles from '../AhaEditorV2.module.css';
import { ApplicationView, useController } from '@aha-app/mvc';
import AhaEditorV2Controller, {
  type ToolbarCompactLevel,
} from '../../controllers/AhaEditorV2Controller';
import ToolbarGroupMenu from './ToolbarGroupMenu';
import ToolbarItem from './ToolbarItem';
import { isToolbarGroup, type ToolbarTool } from './types';
import { useSlate } from 'slate-react';
import { debounce } from 'lodash';
import classNames from 'classnames';
import { cancelEventHandlersOutsideFocusableElements } from '../../util/mouseHandlers';

/*
 * Focus and selection management
 *
 * Handling the focus selection while the user is interacting with the toolbar
 * a challenge. We want focus to move to the toolbar so that is can be
 * accessible - allowing keyboard navigation around the toolbar - but focus
 * should automatically return to the editor when the user is done interacting
 * with the toolbar. Also, we want the selection to still be visible, so
 * as they interact with the toolbar it is obvious what the selection is and
 * so how the toolbar will affect the selection.
 *
 * We are following the technique described here: https://github.com/ianstormtaylor/slate/issues/3412#issuecomment-663906003
 */

interface ToolbarProps {
  toolbarTools: {
    left?: ToolbarTool[];
    right?: ToolbarTool[];
  };
  compactToolbarTools?: {
    left?: ToolbarTool[];
    right?: ToolbarTool[];
  };
  ultraCompactToolbarTools?: {
    left?: ToolbarTool[];
    right?: ToolbarTool[];
  };
  readOnly: boolean;
  compactAtWidth?: number;
  ultraCompactAtWidth?: number;
  setFloatingToolbarHeight?: (height: number) => void;
  floatingToolbarHeight?: number;
}

const Toolbar: React.FC<ToolbarProps> = ({
  toolbarTools,
  compactToolbarTools,
  ultraCompactToolbarTools,
  compactAtWidth,
  ultraCompactAtWidth,
  readOnly,
  setFloatingToolbarHeight,
  floatingToolbarHeight,
}) => {
  const controller = useController(AhaEditorV2Controller);
  const { toolbarCompactLevel, toolbarIsFaded, toolbarRenderId } =
    controller.state;
  const editor = useSlate();
  const toolbarRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!toolbarRef.current) return;

    const resizeObserver = new ResizeObserver(
      debounce((entries: ResizeObserverEntry[]) => {
        if (!Array.isArray(entries)) return;
        if (!toolbarRef.current) return;
        if (!compactAtWidth && !ultraCompactAtWidth) return;

        const hasCompactTools =
          compactToolbarTools?.left?.length ||
          compactToolbarTools?.right?.length;
        const hasUltraCompactTools =
          ultraCompactToolbarTools?.left?.length ||
          ultraCompactToolbarTools?.right?.length;

        const currentWidth = toolbarRef.current.clientWidth;
        let newLevel: ToolbarCompactLevel = 'normal';

        // Determine compact level based on width breakpoints
        if (
          hasUltraCompactTools &&
          ultraCompactAtWidth &&
          currentWidth <= ultraCompactAtWidth
        ) {
          newLevel = 'ultra-compact';
        } else if (
          hasCompactTools &&
          compactAtWidth &&
          currentWidth <= compactAtWidth
        ) {
          newLevel = 'compact';
        }

        controller.actionSetToolbarCompactLevel(newLevel);

        const toolbarHeight = toolbarRef.current.getBoundingClientRect().height;
        if (
          setFloatingToolbarHeight &&
          toolbarHeight !== floatingToolbarHeight
        ) {
          setFloatingToolbarHeight(toolbarHeight);
        }
      }, 20)
    );

    resizeObserver.observe(toolbarRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    compactAtWidth,
    ultraCompactAtWidth,
    controller,
    setFloatingToolbarHeight,
    floatingToolbarHeight,
    compactToolbarTools,
    ultraCompactToolbarTools,
  ]);

  const hasCompactToolbar =
    Array.isArray(compactToolbarTools?.left) ||
    Array.isArray(compactToolbarTools?.right);

  const hasUltraCompactToolbar =
    Array.isArray(ultraCompactToolbarTools?.left) ||
    Array.isArray(ultraCompactToolbarTools?.right);

  let renderToolbarTools = toolbarTools;

  // Select appropriate toolbar configuration based on compact level
  if (toolbarCompactLevel === 'ultra-compact' && hasUltraCompactToolbar) {
    renderToolbarTools = ultraCompactToolbarTools;
  } else if (toolbarCompactLevel === 'compact' && hasCompactToolbar) {
    renderToolbarTools = compactToolbarTools;
  }

  function buildToolbarItem(tool: ToolbarTool): JSX.Element | null {
    if (isToolbarGroup(tool)) {
      return (
        <ToolbarGroupMenu
          key={`group-${tool.menu.name}`}
          editor={editor}
          controller={controller}
          group={tool}
          testId={`group-${tool.menu.name}`}
        />
      );
    } else if (tool.available({ editor, controller })) {
      return (
        <ToolbarItem
          key={tool.toolName}
          editor={editor}
          controller={controller}
          tool={tool}
        />
      );
    }

    return null;
  }

  let leftToolItems: JSX.Element[] = [];
  if (Array.isArray(renderToolbarTools.left)) {
    leftToolItems = renderToolbarTools.left
      .map(buildToolbarItem)
      .filter((t) => t !== null);
  }

  let rightToolItems: JSX.Element[] = [];
  if (Array.isArray(renderToolbarTools.right)) {
    rightToolItems = renderToolbarTools.right
      .map(buildToolbarItem)
      .filter((t) => t !== null);
  }

  const toolbarClasses = classNames(styles.toolbar, editorStyles.toolbar, {
    [styles.fullScreen]: controller.state.pluginState.isFullScreen,
  });
  if (readOnly) return <div className={styles.toolbar} />;

  return (
    <>
      <div
        className={toolbarClasses}
        data-toolbar-render-id={toolbarRenderId}
        ref={toolbarRef}
        {...cancelEventHandlersOutsideFocusableElements}
      >
        <div
          className={classNames(styles.toolbarSection, styles.toolbarItems, {
            [styles.faded]: toolbarIsFaded,
          })}
        >
          {leftToolItems}
        </div>
        <div
          className={classNames(styles.toolbarSection, styles.toolbarItems, {
            [styles.faded]: toolbarIsFaded,
          })}
        >
          {rightToolItems}
        </div>
      </div>
    </>
  );
};

export default ApplicationView(Toolbar);
